import type { ReactElement } from "react"

import useProposalConsumptionSelection from "~/api/hooks/useProposalConsumptionSelection"
import {
  formatCoveragePercent,
  MEASUREMENT_BASIS_LABELS,
  modelingMethodLabel,
  QUALITY_LABELS,
  SOURCE_LABELS,
} from "~/containers/Proposal/Setup/consumption-source-labels"

/**
 * A migrated profile is the usage this proposal already carried before Vision
 * tracked consumption sources. Naming it keeps the seller from reading it as a
 * new source without implying anything editable has changed.
 */
const MIGRATED_SOURCE_NOTE = ` (usage already saved on this proposal)`

const coverageMonthFormat = (timezone: string | null): Intl.DateTimeFormat => {
  const options = { month: `short`, year: `numeric` } as const

  try {
    return new Intl.DateTimeFormat(`en-US`, {
      ...options,
      timeZone: timezone ?? `UTC`,
    })
  } catch {
    // An unrecognized provider timezone must degrade to UTC, not crash Setup.
    return new Intl.DateTimeFormat(`en-US`, { ...options, timeZone: `UTC` })
  }
}

const coverageWindow = (
  start: string | null,
  end: string | null,
  timezone: string | null,
): string | null => {
  if (!start || !end) return null

  // Month boundaries are local to the profile's timezone, and coverageEndAt is
  // an exclusive boundary — step back inside the covered period so the label
  // names the last covered month, not the next one.
  const formatter = coverageMonthFormat(timezone)
  const lastCoveredInstant = new Date(new Date(end).getTime() - 1)

  return `${formatter.format(new Date(start))} – ${formatter.format(lastCoveredInstant)}`
}

export const ConsumptionSource = ({
  proposalId,
}: {
  proposalId: string
}): ReactElement | null => {
  const { data, isError, isLoading, refetch } =
    useProposalConsumptionSelection(proposalId)

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading consumption source…
      </p>
    )
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground">
        The consumption source could not be loaded.{` `}
        <button
          className="underline"
          onClick={() => void refetch()}
          type="button"
        >
          Retry
        </button>
      </p>
    )
  }

  if (!data?.selectedProfile) return null

  const profile = data.selectedProfile
  const isMigrated = profile.sourceKind === `MIGRATION`
  const isModeled = profile.quality === `MODELED`
  const modeledPeriodCount = profile.modeledPeriods?.length ?? 0
  const coverage = formatCoveragePercent(profile.coveragePercent)
  const observedWindow = coverageWindow(
    profile.coverageStartAt,
    profile.coverageEndAt,
    profile.timezone,
  )
  const isLocked = Boolean(data.selection?.lockedAt)

  const details = [
    QUALITY_LABELS[profile.quality],
    MEASUREMENT_BASIS_LABELS[profile.measurementBasis],
    coverage,
    observedWindow,
    isLocked ? `Locked` : null,
  ].filter(Boolean)

  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">
        Current source: {SOURCE_LABELS[profile.sourceSystem]}
        {isMigrated ? MIGRATED_SOURCE_NOTE : null}
      </p>
      <p className="text-sm text-muted-foreground">{details.join(` · `)}</p>
      {isModeled ? (
        <p
          className="rounded-xl border border-warning/40 bg-warning/15 px-3 py-2 text-sm text-foreground"
          role="note"
        >
          {modeledPeriodCount > 0
            ? `Modeled usage: ${modeledPeriodCount} months were filled by modeling (${modelingMethodLabel(profile.modelingMethod)}).`
            : `Modeled usage: values come from modeling (${modelingMethodLabel(profile.modelingMethod)}), not a full year of measurements.`}
        </p>
      ) : null}
    </div>
  )
}
